import ContentLoader from 'react-content-loader'
import { useReducedMotion } from '@/app/motion/useReducedMotion'
import { cn } from '@/lib/utils'

/**
 * Paper-toned skeletons (react-content-loader, DNA loading token: 1200 ms shimmer, speed
 * 1.2), one per region, shown only while a region's data is absent: a cold launch past
 * the 1500 ms cap, or the chart chunk still loading. Static under reduced motion. The
 * hairline tone is the resting colour, the page tone the sweep, so nothing on the page is
 * lighter or darker than paper.
 */

const SPEED = 1.2
// Resting tone: the hairline (visible on paper and on the surface band); the sweep is the
// page tone, so the shimmer is a glint of lighter paper, never a colour.
const BACKGROUND = 'var(--color-hairline)'
const FOREGROUND = 'var(--color-bg)'
const TITLE = 'Menyiapkan data…'

/*
 * The loader clips a gradient with the shapes it is given, and SVG honours only direct
 * shape children of a <clipPath>: a <g> wrapper is ignored. Every shape below is therefore
 * a direct child with its own key.
 */

interface LoaderProps {
  /** Distinct per instance: it seeds the gradient and mask ids. */
  uniqueKey: string
  width: number
  height: number
  className?: string | undefined
  children: React.ReactNode
}

function Loader({ uniqueKey, width, height, className, children }: LoaderProps) {
  const reduced = useReducedMotion()
  return (
    <ContentLoader
      uniqueKey={uniqueKey}
      speed={SPEED}
      animate={!reduced}
      backgroundColor={BACKGROUND}
      foregroundColor={FOREGROUND}
      title={TITLE}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      className={cn('block w-full', className)}
      style={{ height }}
    >
      {children}
    </ContentLoader>
  )
}

interface Slot {
  id: string
  n: number
}

function slots(count: number, prefix: string): Slot[] {
  return Array.from({ length: count }, (_, n) => ({ id: `${prefix}${n}`, n }))
}

/** The five figures band, before the aggregates arrive: five numbers and their labels. */
export function FiguresSkeleton({ className }: { className?: string }) {
  const columns = slots(5, 'angka')
  return (
    <div className={cn('border-y border-hairline bg-surface', className)} data-skeleton="angka">
      <Loader uniqueKey="sk-angka-md" width={1000} height={88} className="hidden md:block">
        {columns.flatMap((c) => [
          <rect key={`${c.id}-angka`} x={16 + c.n * 200} y={16} width={96} height={28} />,
          <rect key={`${c.id}-label`} x={16 + c.n * 200} y={56} width={120} height={13} />,
        ])}
      </Loader>
      <Loader uniqueKey="sk-angka-sm" width={400} height={224} className="md:hidden">
        {columns.flatMap((c) => [
          <rect
            key={`${c.id}-angka`}
            x={16 + (c.n % 2) * 200}
            y={16 + Math.floor(c.n / 2) * 72}
            width={80}
            height={24}
          />,
          <rect
            key={`${c.id}-label`}
            x={16 + (c.n % 2) * 200}
            y={52 + Math.floor(c.n / 2) * 72}
            width={110}
            height={13}
          />,
        ])}
      </Loader>
    </div>
  )
}

/** A hairline table: header band and `rows` rows at the rapat height. */
export function TableSkeleton({ rows = 6, className }: { rows?: number; className?: string }) {
  const height = 40 + rows * 40
  return (
    <Loader uniqueKey={`sk-tabel-${rows}`} width={600} height={height} className={className}>
      <rect x={0} y={0} width={600} height={40} />
      {slots(rows, 'baris').flatMap((r) => [
        <rect key={`${r.id}-nama`} x={8} y={52 + r.n * 40} width={180} height={14} />,
        <rect key={`${r.id}-a`} x={300} y={52 + r.n * 40} width={48} height={14} />,
        <rect key={`${r.id}-b`} x={380} y={52 + r.n * 40} width={48} height={14} />,
        <rect key={`${r.id}-c`} x={460} y={52 + r.n * 40} width={40} height={14} />,
        <rect key={`${r.id}-d`} x={530} y={52 + r.n * 40} width={62} height={14} />,
        <rect key={`${r.id}-garis`} x={0} y={79 + r.n * 40} width={600} height={1} />,
      ])}
    </Loader>
  )
}

const BARS = [0.55, 0.8, 0.4, 0.95, 0.6, 0.7, 0.3].map((h, n) => ({ id: `bar${n}`, n, h }))

/** One chart region: a few bars of uneven height on a baseline. */
export function ChartSkeleton({
  height = 220,
  className,
}: {
  height?: number
  className?: string
}) {
  const width = 400
  const top = 12
  const base = height - 28
  return (
    <Loader uniqueKey={`sk-grafik-${height}`} width={width} height={height} className={className}>
      {BARS.flatMap((bar) => {
        const barHeight = Math.round((base - top) * bar.h)
        return [
          <rect
            key={`${bar.id}-batang`}
            x={24 + bar.n * 52}
            y={base - barHeight}
            width={32}
            height={barHeight}
          />,
          <rect key={`${bar.id}-label`} x={24 + bar.n * 52} y={base + 10} width={32} height={10} />,
        ]
      })}
      <rect x={16} y={base} width={width - 32} height={1} />
    </Loader>
  )
}

/** A list of `rows` lines: the activity list. */
export function ListSkeleton({ rows = 10, className }: { rows?: number; className?: string }) {
  const height = rows * 40
  return (
    <Loader uniqueKey={`sk-daftar-${rows}`} width={600} height={height} className={className}>
      {slots(rows, 'daftar').flatMap((r) => [
        <rect key={`${r.id}-waktu`} x={0} y={13 + r.n * 40} width={40} height={14} />,
        <rect key={`${r.id}-siapa`} x={56} y={13 + r.n * 40} width={64} height={14} />,
        <rect
          key={`${r.id}-apa`}
          x={136}
          y={13 + r.n * 40}
          width={180 + ((r.n * 53) % 160)}
          height={14}
        />,
        <rect key={`${r.id}-garis`} x={0} y={39 + r.n * 40} width={600} height={1} />,
      ])}
    </Loader>
  )
}
